import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import { api } from '../../../utils/api';
import type { Order } from '../../../types/@types/order';
import { TAKEAWAY_ORDERS_QUERY_KEY } from '../../../hooks/orders';
import { ordersKeys } from '../api/ordersKeys';

export interface CancelOrderParams {
  orderId: string | number;
  tableId?: string | number | null;
}

async function cancelOrderRequest(orderId: string | number) {
  if (!orderId) {
    throw new Error('Invalid order id');
  }
  await api.delete(`/orders/${orderId}`);
  return orderId;
}

export function useCancelOrder() {
  const queryClient = useQueryClient();

  return useMutation<string | number, AxiosError<{ message?: string }>, CancelOrderParams>({
    mutationFn: ({ orderId }) => cancelOrderRequest(orderId),
    onSuccess: (orderId, variables) => {
      toast.success('Pedido cancelado');

      const removeFromList = (key: unknown) => {
        queryClient.setQueryData<Order[] | undefined>(key as any, (current = []) =>
          current.filter((order) => order.id.toString() !== orderId.toString()),
        );
      };

      removeFromList(TAKEAWAY_ORDERS_QUERY_KEY);
      queryClient
        .getQueriesData<Order[]>({ queryKey: ordersKeys.takeaway.listBase })
        .forEach(([key]) => removeFromList(key));

      if (variables.tableId) {
        removeFromList(ordersKeys.table(variables.tableId.toString()));
      }

      queryClient.removeQueries({ queryKey: ordersKeys.takeaway.detail(orderId.toString()) });
      queryClient.removeQueries({ queryKey: ordersKeys.detail(orderId.toString()) });
      queryClient.removeQueries({ queryKey: ordersKeys.byId(orderId.toString()) });

      queryClient.invalidateQueries({ queryKey: ordersKeys.all });
      queryClient.invalidateQueries({ queryKey: ordersKeys.takeaway.listBase });
      queryClient.invalidateQueries({ queryKey: ['posTables'] }); // Added
      if (variables.tableId) {
        queryClient.invalidateQueries({ queryKey: ordersKeys.table(variables.tableId.toString()) });
      }
    },
    onError: (error) => {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('No se pudo cancelar el pedido');
      }
    },
  });
}
